import { useState, useEffect } from 'react';
import Table from '../components/common/Table.jsx';
import KpiRow from '../components/optimization/KpiRow.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { exportCSV, getScopedItem, setScopedItem, STORAGE_KEYS } from '../components/common/Helper';

import RoiApiService from '../services/RoiApiService';
import DebugService from '../services/DebugService';
import { useAuth } from '../state/AuthContext';

// ── Filter options ───────────────────────────────────────────

const TERM_OPTIONS = [
  { value: 'P1Y', label: '1-year' },
  { value: 'P3Y', label: '3-year' },
];
const SCOPE_OPTIONS = [
  { value: 'Shared', label: 'Shared' },
  { value: 'Single', label: 'Single' },
];
const LOOKBACK_OPTIONS = [
  { value: 'Last7Days',  label: 'Last 7 days' },
  { value: 'Last30Days', label: 'Last 30 days' },
  { value: 'Last60Days', label: 'Last 60 days' },
];

// ── Helpers ──────────────────────────────────────────────────

const money = (v) => `$${Number(v ?? 0).toFixed(2)}`;
const pct   = (v) => `${Number(v ?? 0).toFixed(2)}%`;

// ── KPI builder ──────────────────────────────────────────────

// Cards mirror the backend: only show a card when its underlying value is
// actually present — never fabricate $0.00 for missing fields.
const buildKpiData = (data) => {
  if (!data) return [];
  const kpis = [];

  if (data.costWithoutBenefit != null) {
    kpis.push({
      id: 1, type: 't1',
      title: 'Before Recommended Purchase',
      value: money(data.costWithoutBenefit),
      subtitle: 'Current monthly on-demand spend',
    });
  }

  const rec = data.recommendationDetails;
  if (rec) {
    const savingsPct = Number(rec.savingsPercentage ?? 0);
    kpis.push({
      id: 2, type: 't2',
      title: 'After Recommended Purchase',
      value: money(rec.totalCost),
      subtitle: 'Estimated monthly spend',
    });
    kpis.push({
      id: 3, type: 't3',
      title: 'Estimated Monthly Savings',
      value: money(rec.savingsAmount),
      subtitle: savingsPct > 0 ? `${savingsPct.toFixed(1)}% lower with recommended plan` : 'With recommended plan',
    });
  }

  return kpis;
};

// ── Table Columns ────────────────────────────────────────────

const roiColumns = [
  { key: 'averageUtilizationPercentage', label: 'Avg Utilization', className: 'num', render: (r) => pct(r.averageUtilizationPercentage) },
  { key: 'coveragePercentage',           label: 'Coverage',        className: 'num', render: (r) => pct(r.coveragePercentage) },
  { key: 'commitmentAmount',             label: 'Commitment',      className: 'num', render: (r) => `${money(r.commitmentAmount)}/hr` },
  { key: 'overageCost',                  label: 'Avg Cost',        className: 'num', render: (r) => money(r.overageCost) },
  { key: 'benefitCost',                  label: 'Benefit',         className: 'num', render: (r) => money(r.benefitCost) },
  {
    key: 'savingsAmount', label: 'Savings', className: 'num',
    render: (r) => <span style={{ color: 'var(--green)', fontWeight: 700 }}>{money(r.savingsAmount)}</span>,
  },
  { key: 'savingsPercentage', label: 'Savings %',    className: 'num', render: (r) => pct(r.savingsPercentage) },
  { key: 'totalCost',         label: 'Total Cost',   className: 'num', render: (r) => money(r.totalCost) },
  { key: 'wastageCost',       label: 'Wastage Cost', className: 'num', render: (r) => money(r.wastageCost) },
];

// ── Main Page ────────────────────────────────────────────────

const AzureRoi = () => {
  const { selectedAccount } = useAuth();

  const [data,      setData]      = useState({});
  const [rows,      setRows]      = useState([]);
  const [kpi,       setKpi]       = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    scope: 'Shared',
    lookBackPeriod: 'Last30Days',
    term: 'P1Y',
  });

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const fetchData = () => {
    if (!selectedAccount) return;
    setIsLoading(true);

    const dataRequest = { ...formData, userAccountId: selectedAccount };
    const userId = getUserId();
    if (userId) dataRequest.userId = userId;

    DebugService.log('Azure ROI: request', dataRequest);

    RoiApiService.getAzureSavingPlans(dataRequest)
      .then((res) => {
        const apiData = res?.data ?? {};
        if (res?.error) {
          DebugService.error('Azure ROI: error in response', res.error);
          setData({}); setRows([]); setKpi([]);
          return;
        }
        setData(apiData);
        setRows((apiData.allRecommendationDetails ?? []).map((d, i) => ({ id: i, ...d })));
        setKpi(buildKpiData(apiData));
      })
      .catch((err) => {
        DebugService.error('Azure ROI: error fetching data', err);
        setData({}); setRows([]); setKpi([]);
      })
      .finally(() => setIsLoading(false));
  };

  // Refetch whenever the account or any recommendation option changes.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, formData]);

  // Keep AI chatbot context in sync with what's on screen.
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, data);
  }, [data]);

  const handleOption = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleExport = () => exportCSV(rows, roiColumns, `azure-roi-${formData.term}-${formData.scope}.csv`);

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Azure ROI</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Azure ROI — Savings Plans{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{rows.length} recommendations</span>}
          </h1>
          <div className="meta">Compute Savings Plan recommendations · commit to reserved capacity to cut on-demand spend</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={fetchData}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={kpi} loading={isLoading} />

      {/* Recommendation options */}
      <div className="filter-bar">
        <div className="field">
          <label>Savings Plan Term</label>
          <select value={formData.term} onChange={(e) => handleOption('term', e.target.value)}>
            {TERM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Scope</label>
          <select value={formData.scope} onChange={(e) => handleOption('scope', e.target.value)}>
            {SCOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Based on the last</label>
          <select value={formData.lookBackPeriod} onChange={(e) => handleOption('lookBackPeriod', e.target.value)}>
            {LOOKBACK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="spacer" />
        <button className="btn ghost" onClick={fetchData}>↻ Refresh</button>
      </div>

      <Table
        rows={rows}
        columns={roiColumns}
        loading={isLoading}
        skeletonRows={6}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="savingsAmount"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No savings plan recommendations for the selected options."
      />
    </>
  );
};

export default AzureRoi;
